import { useCallback, useEffect, useMemo, useRef, useState } from "react"

interface UseVirtualizationOptions<T> {
  items: T[]
  itemHeight: number
  overscan?: number
}

interface UseVirtualizationResult<T> {
  visibleItems: T[]
  containerRef: React.RefObject<HTMLDivElement>
  totalHeight: number
  offsetTop: number
}

export function useVirtualization<T>({
  items,
  itemHeight,
  overscan = 3,
}: UseVirtualizationOptions<T>): UseVirtualizationResult<T> {
  const containerRef = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [containerHeight, setContainerHeight] = useState(0)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleScroll = () => {
      setScrollTop(container.scrollTop)
    }

    const handleResize = () => {
      setContainerHeight(container.clientHeight)
    }

    container.addEventListener("scroll", handleScroll)
    window.addEventListener("resize", handleResize)
    handleResize()

    return () => {
      container.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  const { visibleItems, offsetTop } = useMemo(() => {
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan)
    const endIndex = Math.min(
      items.length,
      Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan,
    )

    return {
      visibleItems: items.slice(startIndex, endIndex),
      offsetTop: startIndex * itemHeight,
    }
  }, [items, scrollTop, containerHeight, itemHeight, overscan])

  const totalHeight = items.length * itemHeight

  return {
    visibleItems,
    containerRef: containerRef as React.RefObject<HTMLDivElement>,
    totalHeight,
    offsetTop,
  }
}

interface UseInfiniteScrollOptions {
  initialCount?: number
  increment?: number
  threshold?: number
}

export function useInfiniteScroll<T>(
  items: T[],
  options: UseInfiniteScrollOptions = {},
): { visibleItems: T[]; sentinelRef: React.RefObject<HTMLDivElement>; hasMore: boolean } {
  const { initialCount = 12, increment = 8, threshold = 0 } = options
  const [visibleCount, setVisibleCount] = useState(initialCount)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const isLoadingRef = useRef(false)
  const rafIdRef = useRef<number | null>(null)

  const loadMore = useCallback(() => {
    if (isLoadingRef.current) return
    isLoadingRef.current = true
    setVisibleCount((prev) => {
      const newCount = Math.min(prev + increment, items.length)
      rafIdRef.current = requestAnimationFrame(() => {
        isLoadingRef.current = false
      })
      return newCount
    })
  }, [increment, items.length])

  // Reset visible count when items array changes (not just length)
  // biome-ignore lint/correctness/useExhaustiveDependencies: items array reference change indicates new data set
  useEffect(() => {
    setVisibleCount(initialCount)
    isLoadingRef.current = false
  }, [items, initialCount])

  // Cleanup requestAnimationFrame on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    if (visibleCount >= items.length) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting && visibleCount < items.length) {
          loadMore()
        }
      },
      {
        threshold,
        rootMargin: "200px 0px",
      },
    )

    observer.observe(sentinel)

    return () => {
      observer.disconnect()
    }
  }, [visibleCount, items.length, loadMore, threshold])

  return {
    visibleItems: items.slice(0, visibleCount),
    sentinelRef: sentinelRef as React.RefObject<HTMLDivElement>,
    hasMore: visibleCount < items.length,
  }
}
